import React, { useEffect, useState } from "react";
import InfoCard from "../../components/ui/InfoCard/InfoCard";
import { FaUniversity, FaSearch } from "react-icons/fa";
import { formService, studiesService } from "../../service";
import Spinner from "../../components/ui/Spinner/Spinner";
import NewResponseModal from "../../components/document/NewResponse/NewResponse";
import InfoStudy from "../../components/Study/InfoStudy/InfoStudy";
import MyResponse from "../../components/document/MyResponse/MyResponse"; 
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import "./FormsPage.css";

const FormsPage = () => {
  const [formsByStudy, setFormsByStudy] = useState({});
  const [studies, setStudies] = useState([]);
  const [formCounts, setFormCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterBy, setFilterBy] = useState("title");

  const [showModal, setShowModal] = useState(false);
  const [selectedForm, setSelectedForm] = useState(null);

  // For InfoStudy offcanvas
  const [showStudyInfo, setShowStudyInfo] = useState(false);
  const [selectedStudy, setSelectedStudy] = useState(null);

  // For switching to MyResponse view
  const [showMyResponse, setShowMyResponse] = useState(false);
  const [selectedFormId, setSelectedFormId] = useState(null);

  const openResponseModal = (form) => {
    setSelectedForm(form);
    setShowModal(true);
  };

  const closeResponseModal = () => {
    setShowModal(false);
    setSelectedForm(null);
  };

  const openStudyInfo = (study) => {
    setSelectedStudy(study);
    setShowStudyInfo(true);
  };

  const closeStudyInfo = () => {
    setShowStudyInfo(false);
    setSelectedStudy(null);
  };

  //  Switch view to MyResponse
  const openMyResponse = (formId) => {
    setSelectedFormId(formId);
    setShowMyResponse(true);
  };

  const backToForms = () => {
    setShowMyResponse(false);
    setSelectedFormId(null);
  };

  useEffect(() => {
    const fetchStudiesAndForms = async () => {
      try {
        setLoading(true);
        const { data: studiesList } = await studiesService.getMine();
        setStudies(studiesList);

        const grouped = {};
        const counts = {};

        for (const study of studiesList) {
          try {
            const { data: forms } = await formService.getAllByStudy(study.id);
            grouped[study.id] = forms;

            // Fetch document count per form
            for (const form of forms) {
              try {
                const countRes = await formService.countDocuments(form.id);
                counts[form.id] = countRes.data.document_count;
              } catch (countErr) {
                console.error(
                  `Erreur lors du comptage des documents pour ${form.title}:`,
                  countErr
                );
                counts[form.id] = 0;
              }
            }
          } catch (err) {
            console.error(
              `Erreur lors du chargement des formulaires pour ${study.name}:`,
              err
            );
            grouped[study.id] = [];
          }
        }

        setFormsByStudy(grouped);
        setFormCounts(counts);
      } catch (error) {
        console.error("Erreur lors du chargement des études:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStudiesAndForms();
  }, []);

  if (loading) return <Spinner size="lg" />;

  const filterForms = (forms, studyName) => {
    return forms.filter((form) => {
      const fieldToFilter = filterBy === "title" ? form.title : studyName || "";
      return fieldToFilter.toLowerCase().includes(searchTerm.toLowerCase());
    });
  };

  //  Switch view: if showMyResponse, render that component instead
  if (showMyResponse && selectedFormId) {
    return <MyResponse formId={selectedFormId} onBack={backToForms} />;
  }

  return (
    <>
      <main className="content overflow-y-auto flex-grow-1 px-4 pb-4 ms-2">
        {/* Header */}
        <div className="sticky-search d-flex align-items-center gap-2 mb-3 pt-4">
          <div className="search-bar-details flex-grow-1 d-flex align-items-center justify-content-between">
            <div className="search-group">
              <input
                type="text"
                placeholder="Chercher un formulaire..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              <button className="icon-btn">
                <FaSearch />
              </button>
            </div>
            <div className="select-group">
              <select
                value={filterBy}
                onChange={(e) => setFilterBy(e.target.value)}
              >
                <option value="title">Par titre de formulaire</option>
                <option value="study">Par nom de l'étude</option>
              </select>
            </div>
          </div>
        </div>

        {/* Render forms grouped by study */}
        {studies.length === 0 ? (
          <p>Aucune étude trouvée.</p>
        ) : (
          studies.map((study) => {
            const forms = formsByStudy[study.id] || [];
            const filtered = filterForms(forms, study.name || study.title);
            if (filtered.length === 0) return null;

            return (
              <section
                key={study.id}
                className="row d-flex align-items-center mb-5 mt-4"
              >
                <div className="col-8 d-flex justify-content-start">
                  <h4 className="ms-1">Étude : {study.name || study.title}</h4>
                </div>
                <div className="col-4 d-flex justify-content-end">
                  <FontAwesomeIcon
                    icon={faCircleInfo}
                    className="details-icon"
                    onClick={() => openStudyInfo(study)}
                    style={{ cursor: "pointer", color: "rgba(77,100,102,1)" }}
                  />
                </div>
                <hr className="mb-4 separator" />

                {filtered.map((form) => (
                  <div className="col-12 col-sm-6 col-md-4 col-lg-3 mb-3" key={form.id}>
                    <InfoCard
                      title={form.title}
                      Icon={FaUniversity}
                      actionType="Mes réponses"
                      count={formCounts[form.id] ?? 0}
                      unit="Documents"
                      type="visitor"
                      onStatsClick={() => openResponseModal(form)}
                      onClick={() => openMyResponse(form.id)} 
                      showOptions={false}
                    />
                  </div>
                ))}
              </section>
            );
          })
        )}
      </main>

      {/* --- Response Modal --- */}
      <NewResponseModal
        show={showModal}
        onClose={closeResponseModal}
        form={selectedForm}
      />

      {/* --- InfoStudy Offcanvas --- */}
      <InfoStudy
        show={showStudyInfo}
        onClose={closeStudyInfo}
        study={selectedStudy}
      />
    </>
  );
};

export default FormsPage;
